import React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { TablePagination } from '@material-ui/core'
import { EditableRegionTypeLookup } from '../../constants/editableRegionTypeLookup.constants'

type Event = {
    id: number
    title: string
}

type Props = {
    events: Event[]
    selectedEventId?: number
    sortField: string
    sortDirection: 'asc' | 'desc'
    pageNumber: number
    pageSize: number
    totalCount: number
    onSelectingEventId: (
        id: number,
        editableRegionTypeLookup: string | null
    ) => void
    onSort: (field: string) => void
    onPageChange: (newPage: number) => void
}

export const useStyles = makeStyles((theme: Theme) => ({
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        padding: '8px',
        textAlign: 'left',
        borderBottom: '1px solid #ccc',
        cursor: 'pointer',
        userSelect: 'none',
    },
    td: {
        padding: '8px',
        borderBottom: '1px solid #eee',
    },
    thead: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: theme.palette.grey[200],
    },
    row: {
        cursor: 'pointer',
        outline: 'none',
        '&:focus': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    selectedRow: {
        backgroundColor: theme.palette.grey[300],
        fontWeight: 'bold',
    },
    tableDiv: { height: 400, width: '100%', overflowY: 'auto' },
}))

const EventTable = ({
    events,
    selectedEventId,
    sortField,
    sortDirection,
    pageNumber,
    totalCount,
    pageSize,
    onSelectingEventId,
    onSort,
    onPageChange,
}: Props) => {
    const classes = useStyles()
    const selectedId = Number(selectedEventId || -1)
    const handleSort = (field: string) => {
        onSort(field)
    }

    const renderSortArrow = (field: string) =>
        sortField === field ? (sortDirection === 'asc' ? '↑' : '↓') : ''

    if (events.length === 0) {
        return (
            <div role='status' aria-live='polite'>
                No events found.
            </div>
        )
    }

    const columns = [
        { field: 'id', label: 'ID' },
        { field: 'title', label: 'Title' },
    ]

    return (
        <div>
            <div className={classes.tableDiv}>
                <table
                    className={classes.table}
                    role='grid'
                    aria-label='Event list'
                    aria-rowcount={events.length}
                >
                    <thead className={classes.thead}>
                        <tr role='row'>
                            {columns.map(({ field, label }) => (
                                <th
                                    key={field}
                                    id={`${field}-column`}
                                    tabIndex={0}
                                    className={classes.th}
                                    onClick={() => handleSort(field)}
                                    onKeyDown={(
                                        e: React.KeyboardEvent<
                                            HTMLTableCellElement
                                        >
                                    ) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            handleSort(field)
                                        }
                                    }}
                                    aria-sort={
                                        sortField === field
                                            ? sortDirection === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                            : 'none'
                                    }
                                >
                                    {label} {renderSortArrow(field)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {events.map(event => (
                            <tr
                                key={event.id}
                                tabIndex={0}
                                role='row'
                                aria-selected={selectedId === event.id}
                                className={`${classes.row} ${
                                    selectedId === event.id
                                        ? classes.selectedRow
                                        : ''
                                }`}
                                onClick={() =>
                                    onSelectingEventId(
                                        event.id,
                                        EditableRegionTypeLookup.EventData
                                    )
                                }
                                onKeyDown={(
                                    e: React.KeyboardEvent<HTMLTableRowElement>
                                ) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        onSelectingEventId(
                                            event.id,
                                            EditableRegionTypeLookup.EventData
                                        )
                                    }
                                }}
                            >
                                <td role='gridcell' className={classes.td}>
                                    {event.id}
                                </td>
                                <td role='gridcell' className={classes.td}>
                                    {event.title}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <TablePagination
                component='div'
                count={totalCount}
                rowsPerPage={pageSize}
                page={pageNumber}
                onPageChange={(
                    _: React.MouseEvent<HTMLButtonElement> | null,
                    newPage: number
                ) => onPageChange(newPage)}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[]}
            />
        </div>
    )
}

export default EventTable
